import i18n from "../i18n";

export const MINUTES_IN_HOUR = 60;
export const HOURS_IN_DAY = 24;
export const MS_IN_DAY = 86_400_000;
export const MS_IN_HOUR = 3_600_000;
export const THOUSAND_DIVIDER = 1_000;
export const MILLION_DIVIDER = 1_000_000;
export const BILLION_DIVIDER = 1_000_000_000;

// Auto-logout configuration options
export const AUTO_LOGOUT_OPTIONS = {
  NONE: { value: 0, label: 'settings.portfolioSecurity.autoLogout.none', minutes: 0 },
  THIRTY_MINUTES: { value: 30 * 60 * 1000, label: 'settings.portfolioSecurity.autoLogout.thirtyMinutes', minutes: 30 },
  ONE_HOUR: { value: 60 * 60 * 1000, label: 'settings.portfolioSecurity.autoLogout.oneHour', minutes: 60 },
  TWO_HOURS: { value: 2 * 60 * 60 * 1000, label: 'settings.portfolioSecurity.autoLogout.twoHours', minutes: 120 },
  EIGHT_HOURS: { value: 8 * 60 * 60 * 1000, label: 'settings.portfolioSecurity.autoLogout.eightHours', minutes: 480 }
};

export const DEFAULT_AUTO_LOGOUT_SETTING = AUTO_LOGOUT_OPTIONS.THIRTY_MINUTES.value;
// Last user activity, shared across tabs so an idle background tab does not end
// the session the user is still working in.
export const LAST_ACTIVITY = "auto-logout-last-activity";

// Scenario gantt base url
export const SCENARIO_GANTT_ROOT_URL = "process/view/gantt";

// PDF Export Server Configuration
// Always go through the Nginx /pdf-export/ proxy (dev and production).
// Override with REACT_APP_PDF_EXPORT_SERVER_HOST when pointing at a direct host.
export const PDF_EXPORT_SERVER_HOST =
  process.env.REACT_APP_PDF_EXPORT_SERVER_HOST || "/pdf-export/";


// Scenario process validation
export const SCENARIO_NEW_STATUS = "NEW";
export const SCENARIO_PERT_STATUS = "PERT";
export const SCENARIO_GANTT_STATUS = "GANTT";
export const SCENARIO_METRICS_STATUS = "METRICS";
export const SCENARIO_APPROVAL_STATUS = "APPROVAL";
export const SCENARIO_APPROVED_STATUS = "APPROVED";
export const SCENARIO_REJECTED_STATUS = "REJECTED";
export const SCENARIO_PENDING_STATUS = "PENDING";
export const RESOURCE_USAGE_STATUS = "PREPARATION";

// Project lifecycle status
export const PROJECT_BACKLOG_STATUS = "BACKLOG"; // EXEC TO BACKLOG
export const PROJECT_ARCHIVED_STATUS = "ARCHIVED"; // CLOSED TO ARCHIVED
export const PROJECT_PROSPECT_STATUS = "PROSPECT";

// Resource usage status
export const RESOURCE_USAGE_DEMAND = "DEMAND";
export const RESOURCE_USAGE_ACTUALS = "ACTUALS";
export const RESOURCE_USAGE_CAPACITY = "CAPACITY";
export const RESOURCE_USAGE_AVAILABILITY = "AVAILABILITY";
export const RESOURCE_USAGE_PLANNED = "BUDGETED";
export const RESOURCE_USAGE_REMAINING = "REMAINING";
export const RESOURCE_USAGE_ALLOCATION = "ALLOCATION";

// Structure type
export const INDEFINITE_SCOPE = "INDEFINITE";
export const DEFINITE_SCOPE = "DEFINITE";
export const IMPORTED_SCOPE = "IMPORTED";
export const CONVERTIBLE_SCOPE = "CONVERTIBLE";

export const MANUAL_PC = "MANUAL";
export const AUTOMATIC_PC = "AUTOMATIC";
export const FULLY_MANUAL_PC = "FULLY_MANUAL";

export const FAVORITE_STRUCTURE = "FAVORITE";
export const PROJECT_STRUCTURE = "PROJECT";

// Block type
export const LEVEL_BLOCK = "LEVEL";
export const STRUCTURE_BLOCK = "STRUCTURE";
export const SEQUENCE_BLOCK = "SEQUENCE";
export const SCOPE_BLOCK = "SCOPE";
export const HEAD_BLOCK = "HEAD";

// Resources
export const RESOURCE_TYPE_INTERNAL = "INTERNAL";
export const RESOURCE_TYPE_EXTERNAL = "EXTERNAL";

export const RESOURCE_DEFAULT_GROUP = "NONE";

// Dimension types (Space/Stock split — plan v1.4). Values must match the
// backend DimensionalResourceEnum (renamed WORKLOAD->SPACE, CONSUMABLE->STOCK).
export const SPACE_DIMENSION_TYPE = "SPACE";
export const STOCK_DIMENSION_TYPE = "STOCK";

// Allowed units per dimension type (plan v1.4 §1.1bis). Codebase is JS, so these
// are value lists rather than TS unions. Display labels come from i18n (unit.*).
// "m" on SPACE = length. unit/dimension consistency is enforced at the form boundary.
export const SPACE_UNITS = ["m", "m2", "m3", "banc", "poste", "creneau", "unite"];
export const STOCK_UNITS = ["unite", "kg", "m", "m2", "L", "lot"];
export const UNITS_BY_DIMENSION = {
  [SPACE_DIMENSION_TYPE]: SPACE_UNITS,
  [STOCK_DIMENSION_TYPE]: STOCK_UNITS,
};

// Scenario Decision Making Process type
export const SCENARIO_DMP = " DECISION-MAKING PROCESS";
export const PREPARATION_PROCESS = "PREPARATION";
export const EXECUTION_PROCESS = "EXECUTION";

// SChedule view
export const GANTT_VIEW = "gantt";
export const TIME_VIEW = "time";
export const WORK_VIEW = "work";
export const COST_VIEW = "cost";
export const RESOURCE_VIEW = "resource";

// Max constants
// A TEAMS resource's Project Gantt assignment load can exceed 100% (e.g. 2 ETP on
// the same resource = 200%). Must be set as Resource.maxUnits, not just as a column
// `max` — Bryntum's AssignmentGrid reads resource.maxUnits at edit time and
// overrides any column-level max (see ScenarioGanttWrapper.js).
export const MAX_RESOURCE_UNITS = 5000;
// Floor for Quantity, Duration and a DIVIDER denominator. Not zero: the engine
// divides by the denominator, and a zero-duration activity has no meaning.
export const MIN_DIMENSION_VALUE = 0.001;
export const MAX_INPUT_LENGTH = 60;
export const MAX_DESCRIPTION_LENGTH = 250;
export const MAX_SCENARIO_DESCRIPTION_LENGTH = 500;
export const MAX_NAME_LENGTH = 100;
export const MAX_GANTT_DEPENDENCIES = 1500;
export const MAX_PORTFOLIO_NAME_LENGTH = 45;
export const MAX_TERM_NAME_LENGTH = 45;
export const MAX_ATTRIBUTE_NAME_LENGTH = 60;
export const MAX_ATTRIBUTE_FREE_TEXT_LENGTH = 60;
export const MAX_ATTRIBUTE_OPTION_LENGTH = 60;

// Intermediate max constants
export const INTERMEDIATE_MAX_INPUT_LENGTH = 20;
export const INTERMEDIATE_INPUT_LENGTH = 6;

// Max constants
export const BLOCK_WIDTH = 520;
export const BLOCK_HEIGHT = 80;

// Min constants
export const MIN_INPUT_LENGTH = 3;

// Filter period in days
export const USAGE_DAYS_LOOK_AHEAD = 90;
export const TASK_DAYS_LOOK_AHEAD = 20;

//Page size
export const HEIGHT_FOR_TABLE = "100vh";

export const SUMMARY_TYPE = "S";
export const MILESTONE_TYPE = "M";
export const REGULAR_TYPE = "R";

// INPUT TYPE
export const DUPLICATOR = "DUPLICATOR";
export const MULTIPLIER = "MULTIPLIER";
export const DIVIDER = "DIVIDER";
export const CAPEX_INPUT_TYPE = "CAPEX";
export const CONTRACTUAL_DATE_TYPE = "CONTRACTUAL_DATE";

// Pivot chart
export const ENABLE_PIVOT_VIRTUALIZATION = false;

// objective status

export const OBJECTIVE_IN_PROGRESS = "IN PROGRESS";
export const OBJECTIVE_AT_RISK = "AT RISK";
export const OBJECTIVE_NOT_STARTED = "NOT STARTED";
export const OBJECTIVE_REACHED = "REACHED";
export const OBJECTIVE_NOT_REACHED = "NOT REACHED";

export const DATE_DEFAULT_FORMAT = "yyyy-MM-dd";

// 2 Format Digit : 0 is a template, it doesn't mean anything
export const TWO_DECIMALS_FORMAT = "0.##";

export const AAS_PREFIX =
  window.location.hostname === "localhost" ? "/aas" : "/api/aas";
export const PDA_PREFIX =
  window.location.hostname === "localhost" ? "/pda" : "/api/pda";
export const MLHUB_PREFIX =
  window.location.hostname === "localhost" ? "/mlhub" : "/api/mlhub";
export const APIFORMATTER_PREFIX =
  window.location.hostname === "localhost" ? "/af" : "/api/af";
export const PDM_PREFIX = "/api/pdm";

// jwtToken
export const JWT_TOKEN = "jwtToken";

// gantt scenario
export const GANTT_SCENARIO_FILTERS = "ganttScenarioFilters-V6";
export const START_TO_START_TYPE = 0;
export const START_TO_FINISH_TYPE = 1;
export const FINISH_TO_START_TYPE = 2;
export const FINISH_TO_FINISH_TYPE = 3;

export const AS_LATE_AS_POSSIBLE_BRYNTUM_VALUE = "aslateaspossible"
export const AS_SOON_AS_POSSIBLE_BRYNTUM_VALUE = "assoonaspossible"
export const CONSTRAINT_TYPES_ITEMS = [
  { value: "none", text: "None" },
  { value: AS_SOON_AS_POSSIBLE_BRYNTUM_VALUE, text: "As soon as possible" },
  { value: "startnoearlierthan", text: "Start no earlier than" },
  { value: "finishnolaterthan", text: "Finish no later than" },
  { value: AS_LATE_AS_POSSIBLE_BRYNTUM_VALUE, text: "As late as possible" },
];

export { MIN_LAGS, MAX_LAGS } from "./lagLimits";

// project dashboard filter
export const PROJECT_DASHBOARD_FILTER = "projectDashboardFilter";

// portfolio dashboard timeline
export const COLUMNS_FOR_TIMELINE = "columnsForTimeline-V9";
export const TIMELINE_FILTERS = "timelineFilters-V2";
export const FULL_TIMELINE_PROJECTS_FILTER = "fullTimelineProjectsFilter-V2";
export const FULL_TIMELINE_YEAR_FILTER = "fullTimelineYearFilter-V2";
export const FULL_TIMELINE_TAGS_FILTER = "fullTimelineTagsFilter-V2";
export const CUSTOM_TIMELINE_PROJECTS_FILTER =
  "customTimelineProjectsFilter-V2";
export const CUSTOM_TIMELINE_YEAR_FILTER = "customTimelineYearFilter-V2";
export const CUSTOM_TIMELINE_TAGS_FILTER = "customTimelineTagsFilter-V2";
export const CUSTOM_SUPPLY_YEAR_FILTER = "customSupplyYearFilter";
export const TIMELINE_PARAMETERS = "timelineParameters-V2";
export const TIMELINE_GROUPER = "timelineGrouper-V2";
export const FULL_TIMELINE = "fullTimeline";
export const CUSTOM_TIMELINE = "customTimeline";
export const CUSTOM_SUPPLY = "customSupply";
export const PORTFOLIO_DATE_FILTER = "Portfolio_Date_Filter";
export const SELECTED_SHIFTS = "selected-shifts";

// tags
export const BLOCK_TAG = "BLOCK";
export const MID_TAG = "MID";
export const START_TAG = "START";
export const FINISH_TAG = "FINISH";
export const MILESTONE_YES = "Yes";
export const SCENARIO_TAG = "SCENARIO";
export const GROUP_TAG = "GROUP";

// project milestones 
export const PROJECT_START_MILESTONE = "PROJECT START MILESTONE";
export const PROJECT_FINISH_MILESTONE = "PROJECT FINISH MILESTONE";

// Constraint types (backend/frontend mapping)
export const CONSTRAINT_TYPE_LATEST = "LATEST";
export const CONSTRAINT_TYPE_FINISH_NO_LATER_THAN = "finishnolaterthan";
export const CONSTRAINT_TYPE_EARLIEST = "EARLIEST";
export const CONSTRAINT_TYPE_START_NO_EARLIER_THAN = "startnoearlierthan";
export const CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE = "AS_LATE";
export const CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT = "aslateaspossible";
export const CONSTRAINT_TYPE_ASAP = "ASAP";
export const CONSTRAINT_TYPE_ASAP_FRONT = "assoonaspossible";

// SCENARIO SOTRAGE
export const SCENARIO_STORAGE = "SCENARIO_STORAGE";

// Roles
export const USER_ADMIN = "admin";
export const USER_EDITOR = "editor";
export const ROLE_SYSTEM_ADMIN = "ROLE_SYSTEM_ADMIN";
export const ROLE_ADMIN = "ROLE_ADMIN";
export const ROLE_PROJECT_MANAGER = "ROLE_PROJECT_MANAGER";
export const ROLE_RESOURCE_MANAGER = "ROLE_RESOURCE_MANAGER";
export const ROLE_PORTFOLIO_MANAGER = "ROLE_PORTFOLIO_MANAGER";
export const ROLE_VIEWER = "ROLE_VIEWER";
export const ROLE_COLLABORATOR = "ROLE_COLLABORATOR";
export const ROLE_RESOURCE_COORDINATOR = "ROLE_RESOURCE_COORDINATOR";

export const ACTION_BUTTON_COLOR = "fourth btn-sm text-white font-weight-bold ";
export const ACTION_BUTTON_OUTLINE = "outline-fourth btn-sm font-weight-bold ";

// DIMENSIONS
export const OPEX = "Labor cost";
export const CAPEX = "Non-labor cost";
export const MAN_HOURS = "Manhours";
export const MAN_DAYS = "Mandays";
export const FTE = "FTE";

// Height below top page
export const INNER_HEIGHT_BELOW_TOP_PAGE = 96;
export const INNER_HEIGHT_BELOW_DOUBLE_ROW_TOP_PAGE = 126;
export const INNER_HEIGHT_BELOW_TOP_PAGE_AND_TABS = 127;

// ITEM TYPE
export const SCENARIO_BLOCK = "scenario_block";
export const LIBRARY_BLOCK = "library_block";
export const SCENARIO_ACTIVITY = "scenario_activity";
export const LIBRARY_ACTIVITY = "library_activity";
export const SCENARIO_GROUP = "scenario_group";
export const LIBRARY_GROUP = "library_group";
export const LIBRARY_CATEGORY = "library_category";
export const LIBRARY_STRUCTURE = "library_structure";

//ScheduleActivity
export const END_OF_BUSINESS_DAY = 17;

export const BUTTON_MODE_EMBEDDED = "BUTTON_MODE_EMBEDDED";

export const LAYOUT_ONCE = "LAYOUT_ONCE";
export const LAYOUT_ALWAYS = "LAYOUT_ALWAYS";
export const LAYOUT_NEVER = "LAYOUT_NEVER";

// Split array of values
export const CUSTOM_SPLIT = "/$&%";
export const CUSTOM_SPLIT_WITH_COMMA = ",/$&%,";

// Visual planning
export const ONE_DAY = "1 day";
export const THREE_DAYS = "3 days";
export const ONE_WEEK = "1 week";
export const TWO_WEEKS = "2 weeks";
export const ONE_MONTH = "1 month";

// scenario state
export const SYNC = "SYNC";
export const UNSYNC = "UNSYNC";

// SCENARIO RISKS
export const AT_RISK = "AT_RISK";
export const NOT_AT_RISK = "NOT_AT_RISK";
export const COMPLIANT = "COMPLIANT";
export const OPPORTUNITY = "OPPORTUNITY";
export const SCENARIO_RISKS = [AT_RISK, NOT_AT_RISK, COMPLIANT, OPPORTUNITY];

// page types
export const LIBRARY_PAGE = "LIBRARY_PAGE";
export const SCENARIO_PAGE = "SCENARIO_PAGE";
export const PROJECT_PAGE = "PROJECT_PAGE";

// USAGES TYPES FOR SIMULATION
export const NEW_USAGES = "NEW USAGES";
export const PORTFOLIO_AVAILABILITY = "PORTFOLIO AVAILABILITY";
export const NEW_AVAILABILITY = "NEW AVAILABILITY";

export const MIN_BOTTLENECK = 0;
export const MAX_BOTTLENECK = 20;
export const NULL_BOTTLENECK = -1;

// Resource types
export const TEAMS = "TEAMS";
export const COLLABORATOR = "COLLABORATOR";
export const GENERIC_NONLABOR = "GENERIC_NONLABOR";
export const SPECIFIC_NONLABOR = "SPECIFIC_NONLABOR";
export const PROJECT_BUFFER = "PROJECT_BUFFER";

export const LABOR = "LABOR";
export const NONLABOR = "NONLABOR";
export const DIMENSION = "DIMENSION";

// BUFFERING METHODS
export const NONE = "NONE";

export const UNCOMPRESSED_PROJECT_BUFFER_SCHEDULE =
  "UNCOMPRESSED_PROJECT_BUFFER_SCHEDULE";
export const COMPRESSED_PROJECT_BUFFER_SCHEDULE =
  "COMPRESSED_PROJECT_BUFFER_SCHEDULE";
export const CLASSIC_CRITICAL_CHAIN_SCHEDULE_COMPRESSION =
  "CLASSIC_CRITICAL_CHAIN_SCHEDULE_COMPRESSION";

// MONITORING TYPES
export const BY_DELAY = "BY_DELAY";
export const FIXED_CAPACITY = "FIXED_CAPACITY";

// SCHEDULER FILTERS
export const SCHEDULER_FILTER_DATES = "scheduler_filter_dates";
export const LOOK_AHEAD_CUSTOM_COLUMNS_ENDPOINT_SUFFIX =
  "approved-or-personal/within-interval";
export const SCHEDULER_FILTER_ARE_TEAMS_VIEW =
  "scheduler_filter_are_teams_view";
export const SCHEDULER_FILTER_SELECTED_PROJECTS =
  "scheduler_filter_selected_projects";
export const SCHEDULER_FILTER_SELECTED_TEAMS =
  "scheduler_filter_selected_teams";
export const SCHEDULER_FILTER_DURATION_DRIVEN_BACKLOG_SELECTED =
  "scheduler_filter_duration_driven_backlog_selected";
export const MY_LOOK_AHEAD_PLAN_DATE_CHANGED =
  "MY_LOOK_AHEAD_PLAN_DATE_CHANGED";

// BRYNTUM TOOLS
export const TBAR_SPACER = "->";
export const TASKS = "TASKS";
export const REGULARS = "REGULARS";

export const RESOURCES = "RESOURCES";
export const SPECTRUM = "SPECTRUM";
export const SPECTRUM_TYPE_WEIGHTED = "WEIGHTED";
export const SPECTRUM_TYPE_REALLOCATION = "REALLOCATION";
export const PORTFOLIO = "portfolio";

export const ALL = "All";

// Non labor dimension resources column name (must match NonLaborResourceColumn.text prefix)
export const NON_LABOR_RESOURCES = i18n.t("term.nonLaborResources");

// confetti
export const SHOW_CONFETTI_EVENT = "show-confetti";

// Resource colors
export const RESOURCE_COLORS = {
  TEAMS: "blue",
  COLLABORATOR: "indigo",
  GENERIC_NONLABOR: "green",
  SPECIFIC_NONLABOR: "teal",
  DIMENSION: "lime",
};

// scenario AI events
export const AI_SCENARIO_ENRICH = "AI_SCENARIO_ENRICH";

// project ask AI events
export const SHOW_PROJECT_IMPORT_DIALOG = "SHOW_PROJECT_IMPORT_DIALOG";

// TaskBoard
export const TODO = "TODO";
export const IN_PROGRESS = "IN_PROGRESS";
export const REVIEW = "REVIEW";
export const DONE = "DONE";
export const LOW = "LOW";
export const MEDIUM = "MEDIUM";
export const HIGH = "HIGH";
export const PERSONAL = "Personal";
export const TASKS_DASHBOARD_SELECTION = "TASKS_DASHBOARD_SELECTION";
export const PROJECTS_DASHBOARD_SELECTION = "PROJECTS_DASHBOARD_SELECTION";
export const USER_TASKBOARD_DATE_FILTER = "User_Taskboard_Date_Filter";
export const USER_TASKBOARD_TEAM_FILTER = "User_Taskboard_Team_Filter";
export const USER_TASKBOARD_LATE_FILTER = "User_Taskboard_Late_Filter";
export const ADD_DEADLINE = "global.addDeadline";
export const USER_TASKBOARD_VIEW = "User_Taskboard_View";
export const USER_TASKBOARD_VIEW_EVENT = "USER_TASKBOARD_VIEW_EVENT";
export const USER_TASKBOARD_VIEW_KANBAN = "USER_TASKBOARD_VIEW_KANBAN";
export const USER_TASKBOARD_VIEW_GRID = "USER_TASKBOARD_VIEW_GRID";
export const USER_TASKBOARD_VIEW_SCHEDULER = "USER_TASKBOARD_VIEW_SCHEDULER";
export const SHOW_USER_TASKBOARD_RESOURCE_UTILIZATION =
  "SHOW_USER_TASKBOARD_RESOURCE_UTILIZATION";
export const TIME_9_AM = "T09:00:00";
export const TIME_5_PM = "T17:00:00";

// Hyppo page types
export const HYPPO_ERROR = "HYPPO_ERROR";
export const HYPPO_NOT_ALLOWED = "HYPPO_NOT_ALLOWED";

// Work Days Calendar
export const DAYS_OF_WEEK = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];
export const DAYS_OF_WEEK_ALT = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];
export const HOLIDAY = "HOLIDAY";
export const EXCEPTION = "EXCEPTION";

export const GANTT_HIDE_COMPLETED_TASKS_FILTER = "HideCompletedTasksFilter";

export const PROJECTS_GRID_ARCHIVED_FILTER = "Projects_Grid_Archived_Filter";

// Bryntum stops iterating a calendar past `project.maxCalendarRange`, and its two
// callers degrade differently: calculateDurationMs ignores the MaxRangeReached
// signal and returns the partial sum (a summary saturated at 1825 calendar days
// -> 1303.57 working days -> 1304 once rounded by the Duration column's "0"
// format), while calculateProjectedXDateWithDuration returns null, leaving an
// assigned task with no finish date so its bar is never rendered at all.
// Raising the range lifts both. Technical ceiling is ~82 years
// (maxCacheExtendCycles 1000 x 30-day caching chunks) - stay well under it.
export const GANTT_MAX_CALENDAR_RANGE_YEARS = 30;
export const GANTT_MAX_CALENDAR_RANGE_MS =
  GANTT_MAX_CALENDAR_RANGE_YEARS * 365 * 24 * 60 * 60 * 1000;

// Editor and DTO ceiling. It must stay below the working days that
// GANTT_MAX_CALENDAR_RANGE_MS can express - 30 y = 10950 calendar days = 7821
// working days at daysPerWeek 5, the lowest conversion and so the binding case.
// A duration the engine cannot reach does not merely clamp: the task loses its
// finish date and disappears, which is what the former 1200 guarded against
// back when the range was Bryntum's 5-year default.
export const MAXIMUM_SAFE_DURATION = 7800;
// Historical Work editor ceiling ("1000000000 hours"); enforced in the
// parse-back paths because unit-expressed editor bounds compare in
// calendar milliseconds and can be bypassed with hour-united input.
export const MAXIMUM_SAFE_EFFORT_HOURS = 1000000000;

// Project entity types
export const PROJECT = "PROJECT";
export const PROGRAM = "PROGRAM";

// calendars hours
export const DEFAULT_START_OF_DAY = 9;

export const VISUAL_PLANNING_VIEW_MODE_ENABLED =
  "VisualPlanningViewModeEnabled";

export const MILESTONE_TIMELINE_ELEMENT = "timeline-milestone";

// program view
export const PROGRAM_GANTT_VIEW = "gantt";
export const PROGRAM_ROADMAP_VIEW = "timeline";
export const PROGRAM_METRICS_VIEW = "metrics";
export const PROGRAM_TIMELINE_STYLE_ID = "program-timeline-style";

// workload page
export const WORKLOAD_FILTER_CHANGE = "WORKLOAD_FILTER_CHANGE";

// accuracy
export const DAILY = "DAILY";
export const WEEKLY = "WEEKLY";
export const MONTHLY = "MONTHLY";

// Resource Usage page
export const RESOURCE_USAGE_GRID_TYPE = "ResourceUsageGridType";
export const RESOURCE_USAGE_GRID_RESOURCE_TYPE = "ResourceUsageGridResourceType";
export const ARE_USAGES_CHANGED = "ARE_USAGES_CHANGED";
export const SAVE_USAGES = "SAVE_USAGES";
export const RESET_USAGES = "RESET_USAGES";

// Resource type filter values (match the BE's ?resourceType= query param).
export const RESOURCE_TYPE_LABOR = "LABOR";
export const RESOURCE_TYPE_NON_LABOR = "NON_LABOR";

// project priority
export const PROJECT_PRIORITIES_VALUES = [
  { name: "MANDATORY", value: 0 },
  { name: "P1", value: 1 },
  { name: "P2", value: 2 },
];

export const EXEC_DATETIME_TIME_RANGE_ID = "exec-datetime-start";

export const MAX_CHARACTERS_NUMBER_FOR_COLUMN_NAME = 60;
export const MAX_LENGTH_FOR_CUSTOM_COLUMN_VALUE = 80;

export const BACKLOG_UPDATE_TYPE_RECORD = "RECORD";
export const BACKLOG_UPDATE_TYPE_CUSTOM_COLUMN = "CUSTOM_COLUMN";
export const BACKLOG_UPDATE_TYPE_EDITING = "EDITING";
export const UPDATE_BACKLOG_DATES = "UPDATE_BACKLOG_DATES";

export const SHARED = "SHARED";
export const SHARED_LABEL = "PUBLIC";
export const PRIVATE = "PRIVATE";

export const TO_BE_PLANNED = "TO_BE_PLANNED";

// Settings Page

export const SETTINGS_KEY_MAPPINGS = {
  portfolioName: "Portfolio Name",
  code: "Portfolio Code",
  monitoringType: "Monitoring Type",
  dateOfReview: "Date of Review",
  remainingMonthsCoefficient: "Remaining Months Coefficient",
  usageLaborAccuracyOption: "Labor Accuracy Option",
  usageNonLaborAccuracyOption: "Non Labor Accuracy Option",
  usageSpaceAccuracyOption: "Space Accuracy Option",
  usageStockAccuracyOption: "Stock Accuracy Option",
  yearlyFTE: "Yearly FTE",
  maxBlocks: "Max Schedule Size",
  startDate: "Start Date",
  finishDate: "Finish Date",
  dailyFTE: "Daily FTE",
  dimensionLimit: `${i18n.t("term.customDimensions")} Limit`,
  isDecimalConsumption: "Decimal Consumption",
  timeResolutionUnit: "Specify your view level ",
  defaultTaskType: "Task type",
  defaultConstraintType: "Constraint type",
  durationDisplayUnit: "Duration unit",
  workDisplayUnit: "Work unit",
  daysPerWeek: "Days per week",
  daysPerMonth: "Days per month",
  hoursPerDay: "Hours per day",
  hoursPerDayUseDailyFte: "Align hours per day with Daily FTE",
  importCalendarsEnabled: "Import calendars from MPP / XER files",
  laborUnitsEnabled: "Show and edit % units in Labor resources",
};

export const SETTINGS_KEY_DESCRIPTIONS = {
  portfolioName: "Choose a portfolio name",
  code: "Unique identifier for your portfolio (auto-generated)",
  monitoringType:
    "Set your portfolio monitoring type. Check documentation for more information",
  dateOfReview: "Chose the date of Review",
  remainingMonthsCoefficient: "Description for Remaining Months Coefficient",
  usageLaborAccuracyOption: `Set usage accuracy for ${i18n.t("term.laborResources").toLowerCase()}`,
  usageNonLaborAccuracyOption: `Set usage accuracy for ${i18n.t("term.nonLaborResources").toLowerCase()}`,
  usageSpaceAccuracyOption: `Set usage accuracy for ${i18n.t("term.space", "Space").toLowerCase()} resources`,
  usageStockAccuracyOption: `Set usage accuracy for ${i18n.t("term.stock", "Stock").toLowerCase()} resources`,
  yearlyFTE: "Number of hours per year for one FTE",
  dailyFTE: "Number of hours per day for one FTE",
  maxBlocks: `Number of ${i18n.t("term.block").toLowerCase()}s per ${i18n.t("term.project").toLowerCase()} scenario`,
  startDate: "Start date of default portfolio calendar",
  finishDate: "Finish date of default portfolio calendar",
  dimensionLimit: `Number of ${i18n.t("term.customDimensions").toLowerCase()} for your portfolio`,
  isDecimalConsumption: `Enable one decimal for ${i18n.t("term.customDimensions").toLowerCase()}`,
};

export const SETTINGS_GENERAL_INFORMATIONS = [
  "portfolioName",
  "code",
  "monitoringType",
];
export const SETTINGS_SECURITY_KEYS = ["autoLogoutTimeout"];
export const SETTINGS_PORTFOLIO_LIMITS_KEYS = [
  "maxBlocks",
  "maxSubCodesPerProject",
  "startDate",
  "finishDate",
  "dimensionLimit",
];
export const SETTINGS_RESOURCE_USAGE_ACCURACY_KEYS = [
  "usageLaborAccuracyOption",
  "usageNonLaborAccuracyOption",
  "usageSpaceAccuracyOption",
  "usageStockAccuracyOption",
  "isDecimalConsumption",
];
export const SETTINGS_FTE_KEYS = ["yearlyFTE", "dailyFTE"];
export const SETTINGS_GANTT_OPTIONS_KEYS = [
  "defaultTaskType",
  "defaultConstraintType",
  "importCalendarsEnabled",
  "laborUnitsEnabled",
];

// Gantt Options → Display Units card. Order = render order.
export const SETTINGS_DISPLAY_UNITS_KEYS = [
  "durationDisplayUnit",
  "workDisplayUnit",
  "daysPerWeek",
  "daysPerMonth",
  "hoursPerDay",
  "hoursPerDayUseDailyFte",
];

// Display/input units for Duration & Work (values mirror the backend
// enums). Storage stays in core units (days / hours); these settings
// only drive the frontend conversion layer (src/utils/displayUnits.js).
export const DURATION_DISPLAY_UNIT_OPTIONS = [
  { value: "DAY", labelKey: "settings.options.durationUnitDay", name: "Day" },
  { value: "WEEK", labelKey: "settings.options.durationUnitWeek", name: "Week" },
  { value: "MONTH", labelKey: "settings.options.durationUnitMonth", name: "Month" },
];
export const WORK_DISPLAY_UNIT_OPTIONS = [
  { value: "HOUR", labelKey: "settings.options.workUnitHour", name: "Hour" },
  { value: "DAY", labelKey: "settings.options.workUnitDay", name: "Day" },
];

// Task type (scheduling mode) options — single source for the block
// properties select, the portfolio Scheduling Options select, and its
// validation. `labelKey` carries the i18n key; `name` is the EN fallback.
export const TASK_TYPE_OPTIONS = [
  { value: "NORMAL", labelKey: "settings.options.taskTypeNormal", name: "Normal" },
  { value: "FIXED_DURATION", labelKey: "settings.options.taskTypeFixedDuration", name: "Fixed Duration" },
  { value: "FIXED_EFFORT", labelKey: "settings.options.taskTypeFixedEffort", name: "Fixed Effort" },
  { value: "FIXED_UNITS", labelKey: "settings.options.taskTypeFixedUnits", name: "Fixed Units" },
];
// Scheduling Options default constraint (values mirror the backend
// ConstraintEnum). Finish No Later Than needs a date and None leaves a task
// unanchored, so neither can be a blanket default.
export const DEFAULT_CONSTRAINT_TYPE_OPTIONS = [
  { value: "ASAP", labelKey: "settings.options.constraintTypeAsap", name: "As Soon As Possible" },
  { value: "EARLIEST", labelKey: "settings.options.constraintTypeStartNoEarlierThan", name: "Start No Earlier Than" },
  { value: "AS_LATE", labelKey: "settings.options.constraintTypeAsLateAsPossible", name: "As Late As Possible" },
];
export const SETTINGS_TASKFOCUS_KEYS = ["taskFocusSettings"];

// API Import Settings
export const SETTINGS_API_IMPORT_KEYS = [
  "enabled",
  "api_base_url",
  "api_url_pattern",
  "auth_type",
  "auth_credentials",
  "timeout_ms",
  "max_retries",
];

// API Import Auth Types
export const AUTH_TYPE_BEARER = "BEARER";
export const AUTH_TYPE_BASIC = "BASIC";
export const AUTH_TYPE_API_KEY = "API_KEY";
export const AUTH_TYPE_API_KEY_AND_BASIC = "API_KEY_AND_BASIC_AUTH";
export const AUTH_TYPE_NONE = "NONE";

// API Key Header Name Options
export const API_KEY_HEADER_X_API_KEY = "X_API_KEY";
export const API_KEY_HEADER_KEY_ID = "KEY_ID";
export const API_KEY_HEADER_API_KEY = "API_KEY";
export const API_KEY_HEADER_AUTHORIZATION_API_KEY = "AUTHORIZATION_API_KEY";

// Default API Key Header
export const DEFAULT_API_KEY_HEADER = API_KEY_HEADER_X_API_KEY;

export const WORK_PACKAGE_GRID_FILTERED_DATA_IDS =
  "WORK_PACKAGE_GRID_FILTERED_DATA_IDS";

export const GANTT_TASK_FOCUS_FILTERS = "GANTT_TASK_FOCUS_FILTERS";
export const GANTT_TASK_FOCUS_FILTER_EVENT = "GANTT_TASK_FOCUS_FILTER_EVENT";
export const SELECTED_TASK_FOCUS_GANTT_CUSTOM_COLUMNS =
  "SELECTED_TASK_FOCUS_GANTT_CUSTOM_COLUMNS";
export const SELECTED_FULL_TIMELINE_CUSTOM_COLUMNS =
  "SELECTED_FULL_TIMELINE_CUSTOM_COLUMNS";
export const SELECTED_PROGRAM_GANTT_CUSTOM_COLUMNS =
  "SELECTED_PROGRAM_GANTT_CUSTOM_COLUMNS";
export const SELECTED_CUSTOM_WORKLOAD_CUSTOM_COLUMNS =
  "SELECTED_CUSTOM_WORKLOAD_CUSTOM_COLUMNS";

// Resource Name Showing (Task Focus)
export const SHOW_RESOURCE_NAME_KEY = "showResourceName";
export const SHOW_CUSTOM_COLUMNS_TOOLTIP =
  "showCustomColumnsTooltipOnTaskFocusGantt";
export const SHOW_CORE_TOOLTIP = "showCoreTooltipOnTaskFocusGantt";

export const TASKFOCUS_SNAP_FEATURE_KEY = "TASKFOCUS_SNAP_FEATURE_KEY";
export const TASKFOCUS_TIME_RESOLUTION = "TASKFOCUS_TIME_RESOLUTION";
export const TASKFOCUS_SHOW_NON_WORKING_TIME =
  "TASKFOCUS_SHOW_NON_WORKING_TIME";

export const RESOURCE_CAPABILITY_GROUP = "CAPABILITIES";
export const RESOURCE_SKILLS_GROUP = "SKILLS";
export const RESOURCE_LOCALIZATIONS_GROUP = "LOCALIZATIONS";
export const RESOURCE_AUTHORIZATION_GROUP = "AUTHORIZATIONS";

// La Hague Constants
export const COLUMN_STATUT_AT = "Statut AT";
export const COLUMN_MP_CP = "MP / CP ?";
export const COLUMN_TYPE_OT = "MP CP CO";


export const INSTRUCTIBLE_COLOR = "#BDD7EE";
export const NON_INSTRUCTIBLE_COLOR = "#FF0000";
export const INSTRUITE_COLOR = "#C6E084";

export const MP_COLOR = "#305496";
export const CP_COLOR = "#7030A0";
export const PRO_COLOR = "#92D050";
export const CO_COLOR = "#C65911";

export const typeTitles = {
  PROGRAM:
    `${i18n.t("term.program")}: A high-level grouping of related ${i18n.t("term.project").toLowerCase()} with a valid scenario and their activities.`,
  SCENARIO:
    `Scenario: A collection of activities representing a specific ${i18n.t("term.project").toLowerCase()} schedule.`,
  GROUP: `Structure : A set of ${i18n.t("term.scope").toLowerCase()}s within a scenario.`,
  SCOPE: `${i18n.t("term.scope")}: A grouping of activities within a scenario.`,
  BLOCK: `${i18n.t("term.block")}: A sub-grouping of activities within a ${i18n.t("term.scope").toLowerCase()}.`,
  BLOCK_ACTIVITY: `${i18n.t("term.block")} Activity: An individual activity within a ${i18n.t("term.block").toLowerCase()}.`,
  ACTIVITY: "Activity: A task to be completed within the plan.",
  MILESTONE: "Milestone: A significant event or checkpoint in the timeline.",
  PSM: `PSM (${i18n.t("term.project")} Start Milestone): Marks the start of a schedule.`,
  PFM: `PFM (${i18n.t("term.project")} Finish Milestone): Marks the end of a schedule.`,
};

export const ActivityTypes = {
  PROGRAM: "PROGRAM",
  SCENARIO: "SCENARIO",
  GROUP: "GROUP",
  PSM: "PSM",
  PFM: "PFM",
  MILESTONE: "MILESTONE",
  SCOPE: "SCOPE",
  BLOCK: "BLOCK",
  BLOCK_ACTIVITY: "BLOCK_ACTIVITY",
  ACTIVITY: "ACTIVITY",
};

export const IconSizes = {
  SMALL: "11px",
  LARGE: "15px",
};

// Bryntum dependency type (0=SS, 1=SF, 2=FS, 3=FF) → spec §5.2 label key.
const LINK_TYPE_LABEL_KEYS = {
  0: "ganttContext.linkTypeStartStart",
  1: "ganttContext.linkTypeStartFinish",
  2: "ganttContext.linkTypeFinishStart",
  3: "ganttContext.linkTypeFinishFinish",
};
const LINK_TYPE_VALUES = [0, 1, 2, 3];

export const linkTypeLabel = (type) => {
  const key = LINK_TYPE_LABEL_KEYS[type];
  return key ? i18n.t(key) : "";
};

// A function, not a constant: labels must follow the language active when a
// combo is built, not the one active when this module was first imported.
export const linkOptions = () =>
  LINK_TYPE_VALUES.map((value) =>
    value === 1
      ? { value, text: linkTypeLabel(value), tooltip: i18n.t("ganttContext.linkTypeStartFinishTooltip") }
      : { value, text: linkTypeLabel(value) }
  );

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Bryntum's combo list has no per-item tooltip config; a title on the item
// markup is how SF's explanation reaches the user on hover.
export const linkTypeListItemTpl = (record) => {
  const read = (field) => (typeof record.get === "function" ? record.get(field) : record[field]);
  const text = escapeHtml(read("text") ?? "");
  const tooltip = read("tooltip");
  return tooltip ? `<div title="${escapeHtml(tooltip)}">${text}</div>` : text;
};

// Kept for callers that read `.text` off a static array; the getter resolves
// the label at read time so those callers follow the language too.
export const LINK_OPTIONS = LINK_TYPE_VALUES.map((value) => ({
  value,
  get text() {
    return linkTypeLabel(value);
  },
}));

